import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { queryClient } from './lib/queryClient'
import App from './App'
import { LocaleProvider } from './i18n/context'
import { prepareFirstPaint } from './i18n/firstPaint'
import './index.css'

// Initialise Sentry before anything renders.
// VITE_SENTRY_DSN is optional — SDK is a no-op when the value is absent.
// tracesSampleRate=0 disables performance monitoring (not needed on free tier).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0,   // error-only; no performance quota consumed
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    environment: import.meta.env.MODE, // "development" | "production"
  })
}

// Register the PWA service worker only in production.
// In development it can cache stale Vite modules and keep old runtime bugs alive.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (import.meta.env.PROD) {
      navigator.serviceWorker
        .register('/sw.js')
        .catch((err) => console.warn('SW registration failed:', err))

      // Reload once the new SW takes control so stale assets are replaced.
      // This fires only when the SW changes, not on first install.
      let firstController = !!navigator.serviceWorker.controller
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (firstController) {
          window.location.reload()
        }
        firstController = true
      })
      return
    }

    // DEV: tear down any registered SW + cached assets left over from a
    // prior production/preview run on this origin. If anything was found,
    // force one reload so that the *current* page (which may have had its
    // JS served cache-first from the stale SW) is discarded.
    // A sessionStorage flag breaks the reload loop: the second load sees
    // no SW and no caches, clears the flag, and proceeds normally.
    ;(async () => {
      const alreadyCleared = sessionStorage.getItem('_sw_dev_cleared')
      if (alreadyCleared) {
        sessionStorage.removeItem('_sw_dev_cleared')
        return
      }

      const [registrations, cacheKeys] = await Promise.all([
        navigator.serviceWorker.getRegistrations().catch(() => [] as ServiceWorkerRegistration[]),
        caches.keys().catch(() => [] as string[]),
      ])
      const staleKeys = cacheKeys.filter((k) => k.startsWith('cardsmith-'))

      if (registrations.length === 0 && staleKeys.length === 0) return

      await Promise.all([
        ...registrations.map((r) => r.unregister().catch(() => {})),
        ...staleKeys.map((k) => caches.delete(k).catch(() => {})),
      ])

      sessionStorage.setItem('_sw_dev_cleared', '1')
      window.location.reload()
    })()
  })
}

// The interface table the first screen needs is loaded before it renders
// (ADR-111): no flash of English for a Russian account.
prepareFirstPaint().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LocaleProvider>
            <App />
          </LocaleProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  )
})
